import { useTranslation } from "react-i18next";

import {
  AlertIcon,
  CheckIcon,
  WarningIcon
} from "@dotnet/telematics-design-system";

import {
  LAST_DOWNLOAD_STATUS,
  ROUTES,
  useDateTime
} from "../../../../../shared";
import {
  DayLimits,
  DownloadStatus,
  DRIVERS_DAY_LIMITS,
  VEHICLES_DAY_LIMITS
} from "../types";

const getDayLimits = (tableLocation: string): DayLimits => {
  return tableLocation === ROUTES.Drivers
    ? DRIVERS_DAY_LIMITS
    : VEHICLES_DAY_LIMITS;
};

const getStatusText = (
  daysAgo: number,
  limits: DayLimits,
  t: Function
): string => {
  if (daysAgo === limits.Today) return t("Table##today");
  if (daysAgo === limits.Safe) return t("Table##day ago");
  return t("Table##days ago", { daysNum: daysAgo });
};

const getDownloadStatusForDays = (
  daysAgo: number,
  limits: DayLimits,
  t: Function
): DownloadStatus => {
  if (daysAgo >= limits.Overdue) {
    return {
      Icon: AlertIcon,
      status: LAST_DOWNLOAD_STATUS.Overdue,
      text: t("Table##Overdue")
    };
  }

  if (daysAgo >= limits.Warning) {
    return {
      Icon: WarningIcon,
      status: LAST_DOWNLOAD_STATUS.Warning,
      text: t("Table##days ago", { daysNum: daysAgo })
    };
  }

  return {
    Icon: CheckIcon,
    status: LAST_DOWNLOAD_STATUS.Safe,
    text: getStatusText(daysAgo, limits, t)
  };
};

export const useLastDownloadFormatting = (
  lastDownloadDate: string,
  tableLocation: string
) => {
  const { t } = useTranslation();
  const { getUTCDate, convertToCurrentDateFormat } = useDateTime();

  const convertedDateForTooltip = lastDownloadDate
    ? convertToCurrentDateFormat(lastDownloadDate)
    : null;

  const tooltipText = t(`Table##last download date`, {
    date: convertedDateForTooltip
  });

  const currentDate = getUTCDate().startOf("day");
  const lastDownloadUTC = getUTCDate(lastDownloadDate).startOf("day");
  const daysAgo = currentDate.diff(lastDownloadUTC, "day");

  const getDownloadStatus = () => {
    const limits = getDayLimits(tableLocation);

    return getDownloadStatusForDays(daysAgo, limits, t);
  };

  return { tooltipText, getDownloadStatus };
};
